import moment from 'moment';
import SimpleModel from './SimpleModel';

/**
 * Represents a candidate with various properties and methods to retrieve formatted data.
 */
class Candidate extends SimpleModel {
    /**
     * Constructs a new Candidate instance.
     * @param {Object} SimpleModel - The simple model object.
     * @param {Function} $interpolate - The AngularJS interpolate service.
     * @param {Function} $translate - The AngularJS translate service.
     * @param {Function} $filter - The AngularJS filter service.
     * @param {Object} ndata - The candidate data.
     */
    constructor(SimpleModel, $interpolate, $translate, $filter, ndata) {
        super(ndata);
        this.SimpleModel = SimpleModel;
        this.$interpolate = $interpolate;
        this.$translate = $translate;
        this.$filter = $filter;
        this.encode = $filter('encodeHtml');        
        console.info("construct", ndata);
        this.init();
    }

    /**
     * Initializes the candidate instance with readable and accessible properties.
     * @returns {Candidate} The current instance.
     */
    init() {
        this.readables({
            'IAA': Object,
            'birthDate': String,
            'candidateNo': String,
            'isPreRegistered': Boolean,
            'isPirate': Boolean,
            'isInvigilator': Boolean,
            'schoolName': String,
            'taskTime': Number,
            'taskUUID': String,
            'type': String,
            'cloudResponseUpload': Boolean
        });
        this.accessables({
            'firstName': String,
            'lastName': String,
            'birthDate': String,
            'schoolCode': String,
            'targetEmail': String
        });
    }

    /**
     * Gets the complete name of the candidate.
     * @param {boolean} [no_html_encoding=false] - Whether to skip HTML encoding.
     * @returns {string} The complete name.
     */
    getCompleteName(no_html_encoding = false) {
        const firstName = this.data.firstName || '';
        const lastName = this.data.lastName || '';
        return no_html_encoding ?
            [firstName, lastName].join(' ') :
            this.encode([firstName, lastName].join(' '));
    }

    /**
     * Gets the school information of the candidate.
     * @param {boolean} [no_html_encoding=false] - Whether to skip HTML encoding.
     * @returns {string} The school code or name.
     */
    getSchool(no_html_encoding = false) {
        const schoolCode = this.data.schoolCode || '';
        const schoolName = this.data.schoolName || '';
        return schoolCode ? schoolCode : no_html_encoding ? schoolName : this.encode(schoolName);
    }

    /**
     * Gets the formatted birth date of the candidate.
     * @returns {string} The formatted birth date.
     */
    getBirth() {
        const birthDate = this.data.birthDate || '';
        return moment(birthDate, 'DD/MM/YYYY').format('L');
    }

    /**
     * Gets the formatted task time.
     * @param {number} [taskTimeMsParam] - The task time in milliseconds.
     * @returns {string} The formatted task time.
     */
    getTaskTimeFormatted(taskTimeMsParam) {
        let taskTimeMs = typeof taskTimeMsParam !== 'undefined' ? taskTimeMsParam : this.data.taskTime || 0;
        if (taskTimeMs < 0) taskTimeMs = 0;

        let seconds = Math.floor((taskTimeMs / 1000) % 60),
            minutes = Math.floor((taskTimeMs / (1000 * 60)) % 60),
            hours = Math.floor((taskTimeMs / (1000 * 60 * 60)) % 24);

        if (seconds === 0 && minutes === 0)
            return hours === 1 ? hours + ' ' + this.$translate.instant('SYSTEM.CLOCK.HOUR') : hours + ' ' + this.$translate.instant('SYSTEM.CLOCK.HOURS');

        if (seconds === 0 && hours === 0)
            return minutes === 1 ? minutes + ' ' + this.$translate.instant('SYSTEM.CLOCK.MINUTE') : minutes + ' ' + this.$translate.instant('SYSTEM.CLOCK.MINUTES');

        if (hours === 0 && minutes === 0)
            return seconds === 1 ? seconds + ' ' + this.$translate.instant('SYSTEM.CLOCK.SECOND') : seconds + ' ' + this.$translate.instant('SYSTEM.CLOCK.SECONDS');

        let result = "hh:mm:ss";

        // Replace hours
        result = result.replace('hh', hours.toString().padStart(2, '0'));

        // Replace minutes
        result = result.replace('mm', minutes.toString().padStart(2, '0'));

        // Replace seconds
        result = result.replace('ss', seconds.toString().padStart(2, '0'));

        return result;
    }
}

export default Candidate;